import React, { Suspense, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ClientReducerState, useClientSelector } from 'client/reducers';
import { selectStoryVariables } from 'client/reducers/story/selectors';

type Props = ConnectedProps<typeof connector> & {
	html: string;
	onChange: (editorState: any) => void;
	onComplete: () => void;
	editableRef: React.RefObject<HTMLDivElement>;
};

const RichEditor = React.lazy(() => import('client/components/common/RichEditor'));

const ContentEditable = ({ html, onChange, onComplete, editableRef, selection }: Props) => {
	const id = editableRef.current?.id ?? '';
	const isSelected = useRef(selection[id] !== undefined);

	/* Get variables only on mount, do not recompute them during text edit.
	   Variables are used on mount of text to display [data-mention] and <MentionSuggestions /> */
	const memoizedVariables = useRef<ReturnType<typeof selectStoryVariables>>([]);
	const variables = useClientSelector(state => {
		if (memoizedVariables.current.length) return memoizedVariables.current;
		memoizedVariables.current = selectStoryVariables(state);
		return memoizedVariables.current;
	});

	useEffect(
		function completeOnSelectionChange() {
			if (isSelected.current && !selection[id]) {
				onComplete();
			}

			isSelected.current = selection[id] !== undefined;
		},
		[onComplete, selection, id]
	);

	return (
		// eslint-disable-next-line react/no-danger
		<Suspense fallback={<div dangerouslySetInnerHTML={{ __html: html }} />}>
			<RichEditor html={html} onChange={onChange} variables={variables} />
		</Suspense>
	);
};

const connector = connect(({ editor }: ClientReducerState) => ({ selection: editor.selection.clicked }));

export default connector(ContentEditable);
